import type {RecordProxy} from 'relay-runtime'

import type {PendingSuggestion} from '../components/recent-agent-activity/use-recent-agent-activity'

/**
 * The apply and reject pending-suggestion mutations accept structurally
 * identical "suggestion refs". We define the union once and derive both the
 * GraphQL input and the optimistic-update store matchers from it, so the two
 * mutations stay in sync.
 */
export type SuggestionRef =
  | {kind: 'TYPE'; issueTypeId: string}
  | {kind: 'LABEL'; labelId: string}
  | {kind: 'FIELD'; issueFieldId: string}
  | {kind: 'CLOSE'}
  | {kind: 'ASSIGNEE'; assigneeId: string}

type SuggestionInput = {
  kind: SuggestionRef['kind']
  issueTypeId?: string
  labelId?: string
  issueFieldId?: string
  assigneeId?: string
}

/** Translate a suggestion ref into the mutation input object. */
export function toSuggestionInput(ref: SuggestionRef): SuggestionInput {
  switch (ref.kind) {
    case 'TYPE':
      return {kind: 'TYPE', issueTypeId: ref.issueTypeId}
    case 'LABEL':
      return {kind: 'LABEL', labelId: ref.labelId}
    case 'FIELD':
      return {kind: 'FIELD', issueFieldId: ref.issueFieldId}
    case 'ASSIGNEE':
      return {kind: 'ASSIGNEE', assigneeId: ref.assigneeId}
    case 'CLOSE':
      return {kind: 'CLOSE'}
  }
}

// Apply and reject refs share the same shape, so one builder serves both mutations.
export function buildSuggestionRef(suggestion: PendingSuggestion): SuggestionRef | null {
  if (suggestion.type === 'type_set' && suggestion.issueTypeId) {
    return {kind: 'TYPE', issueTypeId: suggestion.issueTypeId}
  }
  if (suggestion.type === 'label_added' && suggestion.labelId) {
    return {kind: 'LABEL', labelId: suggestion.labelId}
  }
  if (suggestion.type === 'field_set' && suggestion.issueFieldId) {
    return {kind: 'FIELD', issueFieldId: suggestion.issueFieldId}
  }
  if (suggestion.type === 'issue_closed') {
    return {kind: 'CLOSE'}
  }
  if (suggestion.type === 'assignee_assigned' && suggestion.assigneeId) {
    return {kind: 'ASSIGNEE', assigneeId: suggestion.assigneeId}
  }
  return null
}

type RefDescriptor = {
  /** `__typename` of the matching pending-suggestion record in the Relay store. */
  typename: string
  /** Linked record holding the suggested entity, or null when there is none. */
  linkedRecord: string | null
}

const REF_DESCRIPTORS: Record<SuggestionRef['kind'], RefDescriptor> = {
  TYPE: {typename: 'PendingTypeSuggestion', linkedRecord: 'issueType'},
  LABEL: {typename: 'PendingLabelSuggestion', linkedRecord: 'label'},
  FIELD: {typename: 'PendingFieldSuggestion', linkedRecord: 'issueField'},
  ASSIGNEE: {typename: 'PendingAssigneeSuggestion', linkedRecord: 'assignee'},
  CLOSE: {typename: 'PendingCloseSuggestion', linkedRecord: null},
}

function refEntityId(ref: SuggestionRef): string | null {
  switch (ref.kind) {
    case 'TYPE':
      return ref.issueTypeId
    case 'LABEL':
      return ref.labelId
    case 'FIELD':
      return ref.issueFieldId
    case 'ASSIGNEE':
      return ref.assigneeId
    case 'CLOSE':
      return null
  }
}

/**
 * Invoke `fn` for each pending-suggestion record that matches `ref`: same
 * suggestion `__typename`, same suggesting actor, and (for entity-bearing
 * kinds) the same linked entity id. CLOSE suggestions match on actor alone,
 * since there is at most one pending close suggestion per actor.
 */
export function forEachMatchingPendingSuggestion(
  pendingSuggestions: ReadonlyArray<RecordProxy | null>,
  actorId: string,
  ref: SuggestionRef,
  onMatch: (record: RecordProxy) => void,
): void {
  const descriptor = REF_DESCRIPTORS[ref.kind]
  const entityId = refEntityId(ref)

  for (const record of pendingSuggestions) {
    if (!record) continue
    if (record.getValue('__typename') !== descriptor.typename) continue
    if (record.getLinkedRecord('actor')?.getValue('id') !== actorId) continue

    if (descriptor.linkedRecord && entityId !== null) {
      const linked = record.getLinkedRecord(descriptor.linkedRecord)
      if (linked?.getValue('id') !== entityId) continue
    }

    onMatch(record)
  }
}
